import React, { useState, useEffect } from 'react';

/**
 * Header shown on every Funeral Professional portal surface (landing,
 * auth, dashboard, legal).
 *
 * Design goals:
 *   • Visually distinct from the consumer header so funeral staff know
 *     they are in the commercial product.
 *   • Wordmark + quill icon as the brand — no generic bitmap logo.
 *   • Always-visible primary CTA ("Start free trial" when logged out,
 *     "Go to portal" when already authenticated).
 *   • Breadcrumb-style nav that highlights the current section.
 *   • Pure glass/pastel aesthetic to match the rest of the portal.
 */
const FdHeader = () => {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  // Use `authenticated` to adjust the right-hand CTA without a prop
  // drill. Must match the key the dashboard writes to on login —
  // `fd_token` (see TOKEN_KEY in FuneralDirectorDashboard). Listen for
  // `storage` events so other tabs logging in/out flip this header
  // without a reload.
  const [authenticated, setAuthenticated] = useState(false);
  useEffect(() => {
    const read = () => {
      try {
        setAuthenticated(!!localStorage.getItem('fd_token'));
      } catch (_) { /* noop */ }
    };
    read();
    const onStorage = (e) => {
      if (!e || e.key === 'fd_token' || e.key === null) read();
    };
    window.addEventListener('storage', onStorage);
    // The dashboard dispatches `fd-auth-changed` on login / logout /
    // 401-sweep so the header flips CTA in the same tab instantly.
    window.addEventListener('fd-auth-changed', read);
    // Belt-and-suspenders: re-read on window focus too.
    window.addEventListener('focus', read);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('fd-auth-changed', read);
      window.removeEventListener('focus', read);
    };
  }, []);

  // Highlight the current section.
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const isLanding = path === '/funeral-directors' || path === '/funeral-directors/';
  const isDashboard = path.startsWith('/funeral-directors/dashboard');
  const isLegal =
    path === '/funeral-directors/terms' ||
    path === '/funeral-directors/privacy';

  return (
    <header className="fd-header fd-header-v2">
      <div className="fd-header-inner container">
        {/* Brand — quill icon + gradient wordmark. No bitmap logo. */}
        <a href="/funeral-directors" className="fd-header-brand" onClick={close}>
          <span className="fd-header-mark" aria-hidden="true">
            <svg viewBox="0 0 28 28" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 24 C 10 18 18 10 24 4" />
              <path d="M20 3 C 22 5 24 6 25.5 6.4 C 24.2 10 21.6 13.3 18 16 C 14.4 18.5 10.4 20.4 6.3 21 C 6.7 18.6 8.4 15.6 11 12.8 C 13.8 9.8 17 6.5 20 3 Z" />
              <path d="M13 14 C 14.6 13.8 16.3 13 18 11.5" />
            </svg>
          </span>
          <span className="fd-header-brand-text">
            <span className="fd-header-brand-title">ObitAuthor</span>
            <span className="fd-header-brand-sub">Funeral Professional Portal</span>
          </span>
        </a>

        {/* Mobile toggle */}
        <button
          type="button"
          className="fd-header-toggler"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label="Toggle navigation"
        >
          <span className="fd-header-toggle-bars" aria-hidden="true">
            <span /><span /><span />
          </span>
        </button>

        {/* Nav + CTA */}
        <div className={`fd-header-right ${open ? 'is-open' : ''}`}>
          <nav className="fd-header-nav" aria-label="Portal sections">
            <a
              className={`fd-header-link ${isLanding ? 'is-active' : ''}`}
              href="/funeral-directors"
              onClick={close}
            >
              Program
            </a>
            <a
              className={`fd-header-link ${isDashboard ? 'is-active' : ''}`}
              href={authenticated ? '/funeral-directors/dashboard' : '/funeral-directors/dashboard?mode=register'}
              onClick={close}
            >
              Dashboard
            </a>
            <a
              className={`fd-header-link ${isLegal ? 'is-active' : ''}`}
              href="/funeral-directors/terms"
              onClick={close}
            >
              Legal
            </a>
          </nav>

          <div className="fd-header-cta-group">
            {authenticated ? (
              <a
                className="fd-header-cta"
                href="/funeral-directors/dashboard"
                onClick={close}
              >
                Go to portal
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M5 12h14" /><path d="M13 5l7 7-7 7" />
                </svg>
              </a>
            ) : (
              <>
                <a
                  className="fd-header-login"
                  href="/funeral-directors/dashboard"
                  onClick={close}
                >
                  Log in
                </a>
                <a
                  className="fd-header-cta"
                  href="/funeral-directors/dashboard?mode=register"
                  onClick={close}
                >
                  Start free trial
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M5 12h14" /><path d="M13 5l7 7-7 7" />
                  </svg>
                </a>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default FdHeader;
